import React, { useEffect } from "react";
import { useTable, usePagination } from "react-table";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Box,
    Grid,
    Button,
    Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(() => ({
    tableContainer: {
        boxShadow: "none",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
    },
    headerCell: {
        backgroundColor: "#f8f9fb",
        color: "#5f6368",
        fontWeight: "bold !important",
        fontSize: "0.75rem !important",
        textTransform: "uppercase",
        padding: "12px 16px !important",
        borderBottom: "1px solid #e0e0e0",
    },
    rowCell: {
        fontSize: "0.85rem !important",
        padding: "10px 16px !important",
        color: "#202124",
        borderBottom: "1px solid #f1f3f4",
    },
    searchField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "4px",
            backgroundColor: "transparent",
            "& fieldset": {
                borderColor: "#e0e0e0",
            },
        },
        "& .MuiInputBase-input": {
            padding: "8px 12px",
            fontSize: "0.9rem",
        }
    }
}));

export default function TableC({ columns, data, globalFilter, setGlobalFilter, children }) {
    const classes = useStyles();

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageSize: 100 },
        },
        usePagination
    );

    useEffect(() => {
        setPageSize(100);
    }, [setPageSize]);

    return (
        <Box sx={{ width: '100%', p: 1 }}>
            {/* Header: Search and Filter */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ width: '60%' }}>
                    <TextField
                        className={classes.searchField}
                        variant="outlined"
                        placeholder="Buscador"
                        fullWidth
                        value={globalFilter || ""}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        size="small"
                    />
                </Box>
                <Box>
                    {children}
                </Box>
            </Box>

            <Paper className={classes.tableContainer}>
                <Table {...getTableProps()}>
                    <TableHead>
                        {headerGroups.map((headerGroup, idx) => (
                            <TableRow {...headerGroup.getHeaderGroupProps()} key={idx}>
                                {headerGroup.headers.map((column, colIdx) => (
                                    <TableCell 
                                        {...column.getHeaderProps()} 
                                        key={colIdx}
                                        className={classes.headerCell}
                                    >
                                        {column.render("Header")}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {page.map((row, rowIdx) => {
                            prepareRow(row);
                            return (
                                <TableRow {...row.getRowProps()} key={rowIdx}>
                                    {row.cells.map((cell, cellIdx) => (
                                        <TableCell 
                                            {...cell.getCellProps()} 
                                            key={cellIdx}
                                            className={classes.rowCell}
                                        >
                                            {cell.render("Cell")}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>

            {/* Footer: Pagination Info and Buttons */}
            <Box sx={{ mt: 2, px: 1 }}>
                <Grid container alignItems="center">
                    {/* Left side: Page indicator */}
                    <Grid item xs={6}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#5f6368' }}>
                            Página <strong>{pageIndex + 1}</strong> de <strong>{pageOptions.length}</strong>
                        </Typography>
                    </Grid>

                    {/* Right side: Navigation buttons aligned to the end */}
                    <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            onClick={previousPage}
                            disabled={!canPreviousPage}
                            variant="outlined"
                            size="small"
                            sx={{
                                textTransform: 'none',
                                minWidth: 100,
                                color: '#3c4043',
                                borderColor: '#dadce0',
                                fontWeight: 500,
                                '&:hover': { 
                                    borderColor: '#dadce0', 
                                    backgroundColor: '#f8f9fb' 
                                },
                                '&.Mui-disabled': { 
                                    color: '#bdc1c6', 
                                    borderColor: '#f1f3f4' 
                                }
                            }}
                        >
                            Anterior
                        </Button>

                        <Button
                            onClick={nextPage}
                            disabled={!canNextPage}
                            variant="contained"
                            size="small"
                            disableElevation
                            sx={{
                                textTransform: 'none',
                                minWidth: 100,
                                backgroundColor: '#1a73e8',
                                color: '#fff',
                                fontWeight: 500,
                                '&:hover': { 
                                    backgroundColor: '#1765cc' 
                                },
                                '&.Mui-disabled': { 
                                    backgroundColor: '#f1f3f4', 
                                    color: '#bdc1c6' 
                                }
                            }}
                        >
                            Siguiente
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}